import { Badge } from "@thilke/ui/components/badge";
import { Counter, type CounterTone } from "@thilke/ui/components/counter";
import { Panel } from "@thilke/ui/components/panel";
import {
  Stat,
  StatDescription,
  StatLabel,
  StatValue,
} from "@thilke/ui/components/ui/stat";
import { cn } from "@thilke/ui/lib/utils";
import type * as React from "react";

export interface ActorStat {
  label: string;
  value: React.ReactNode;
  tone?: "default" | "accent" | "muted";
}

export interface ActorResource {
  label: string;
  value: number | string;
  max?: number | string;
  display?: "text" | "pips";
  tone?: CounterTone;
}

export interface ActorSheetProps extends React.ComponentProps<"div"> {
  name: string;
  actorType?: string;
  system?: string;
  portrait?: React.ReactNode;
  stats?: ActorStat[];
  resources?: ActorResource[];
  actions?: React.ReactNode;
}

function ActorSheet({
  name,
  actorType = "Actor",
  system,
  portrait,
  stats = [],
  resources = [],
  actions,
  children,
  className,
  ...props
}: ActorSheetProps) {
  return (
    <Panel
      position="docked"
      size="md"
      className={cn("grid gap-3", className)}
      {...props}
    >
      <header className="grid grid-cols-[44px_1fr_auto] items-start gap-3">
        <div className="flex size-11 items-center justify-center overflow-hidden rounded-md border border-border bg-background text-xs text-muted-foreground">
          {portrait ?? actorType.slice(0, 2).toUpperCase()}
        </div>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            <h2 className="truncate text-sm font-semibold leading-tight">
              {name}
            </h2>
            <Badge variant="outline">{actorType}</Badge>
            {system ? <Badge variant="secondary">{system}</Badge> : null}
          </div>
          {resources.length > 0 ? (
            <div className="mt-2 grid gap-1.5 sm:grid-cols-2">
              {resources.map((resource) => {
                const value = resource.value;
                const max = resource.max;

                if (
                  resource.display === "pips" &&
                  typeof value === "number" &&
                  typeof max === "number"
                ) {
                  return (
                    <div
                      key={resource.label}
                      className="rounded-md border border-border bg-card p-2.5 shadow-none"
                    >
                      <Counter
                        value={value}
                        max={max}
                        label={resource.label.toUpperCase()}
                        tone={resource.tone}
                        size="sm"
                      />
                    </div>
                  );
                }

                return (
                  <Stat
                    key={resource.label}
                    className="rounded-md p-2.5 shadow-none"
                  >
                    <StatLabel className="text-xs uppercase tracking-wide">
                      {resource.label}
                    </StatLabel>
                    <StatValue className="font-mono text-sm">
                      {resource.value}
                      {resource.max !== undefined ? `/${resource.max}` : ""}
                    </StatValue>
                  </Stat>
                );
              })}
            </div>
          ) : null}
        </div>
        {actions ? (
          <div className="flex shrink-0 items-center gap-1.5">{actions}</div>
        ) : null}
      </header>
      {stats.length > 0 ? (
        <div className="grid grid-cols-3 gap-1.5 md:grid-cols-6">
          {stats.map((stat) => (
            <Stat
              key={stat.label}
              className={cn(
                "rounded-md p-2.5 shadow-none",
                stat.tone === "accent" && "border-primary/50",
                stat.tone === "muted" && "opacity-70",
              )}
            >
              <StatLabel className="text-xs uppercase tracking-wide">
                {stat.label}
              </StatLabel>
              <StatValue className="font-mono text-sm">{stat.value}</StatValue>
            </Stat>
          ))}
        </div>
      ) : null}
      {children ? (
        <Stat className="rounded-md p-3 shadow-none">
          <StatDescription className="text-xs leading-normal text-foreground">
            {children}
          </StatDescription>
        </Stat>
      ) : null}
    </Panel>
  );
}

export { ActorSheet };
